import { useCallback, useEffect, useRef, useState } from 'react';
import chatService from '../services/chatService';
import { sanitizeMessageText } from '../utils/sanitizeMessageText';

/**
 * Centralized audio management hook for chat messages
 * Ensures only one audio plays at a time and manages UI state synchronization
 */
export function useAudioManager() {
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const currentMessageIdRef = useRef(null);
  const audioUrlRef = useRef(null);
  const endedHandlerRef = useRef(null);
  const errorHandlerRef = useRef(null);

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      // Remove event listeners
      if (endedHandlerRef.current) {
        audioRef.current.removeEventListener('ended', endedHandlerRef.current);
      }
      if (errorHandlerRef.current) {
        audioRef.current.removeEventListener('error', errorHandlerRef.current);
      }

      // Pause and clean up audio
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();

      // Revoke the object URL
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }

      // Clear refs
      audioRef.current = null;
      endedHandlerRef.current = null;
      errorHandlerRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    cleanupAudio();
    setPlayingMessageId(null);
    currentMessageIdRef.current = null;
  }, [cleanupAudio]);

  const playAudio = useCallback(
    async (messageId, text) => {
      // If same message is playing, stop it
      if (playingMessageId === messageId) {
        stopAudio();
        return;
      }

      // Stop any currently playing audio
      stopAudio();

      // Validate and clean text
      const cleanText = sanitizeMessageText(text);
      if (!cleanText || cleanText.length === 0) {
        setError('No text to play');
        return;
      }

      if (cleanText.length > 5000) {
        setError('Text is too long (max 5000 characters)');
        return;
      }

      setLoadingMessageId(messageId);
      setError(null);

      try {
        const audioBlob = await chatService.getTextAudio(cleanText);
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        audioRef.current = audio;
        currentMessageIdRef.current = messageId;
        audioUrlRef.current = audioUrl;

        // Create event handlers
        endedHandlerRef.current = () => {
          if (currentMessageIdRef.current === messageId) {
            stopAudio();
          }
        };

        errorHandlerRef.current = () => {
          setError('Failed to play audio');
          if (currentMessageIdRef.current === messageId) {
            stopAudio();
          }
        };

        // Add event listeners
        audio.addEventListener('ended', endedHandlerRef.current);
        audio.addEventListener('error', errorHandlerRef.current);

        await audio.play();
        setPlayingMessageId(messageId);
        setLoadingMessageId(null);
      } catch (error) {
        console.error('Error playing audio:', error);
        setError(error.message || 'Failed to load audio');
        setLoadingMessageId(null);
      }
    },
    [playingMessageId, stopAudio]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return {
    playAudio,
    stopAudio,
    playingMessageId,
    loadingMessageId,
    error,
    isPlaying: (messageId) => playingMessageId === messageId,
    isLoading: (messageId) => loadingMessageId === messageId,
  };
}
